import { useEffect } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { useFormContext, Controller } from 'react-hook-form';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useDispatch, useSelector } from 'react-redux';
import { getFile } from 'app/main/apps/e-commerce/store/filesSlice';
import FormHelperText from '@mui/material/FormHelperText';
import { Typography } from '@mui/material';
import { HelperTooltipWithIcon } from 'app/main/apps/common/AuraToolTip';
import { calculateProductTotalQuantity, calculateSquareFeet } from 'app/main/apps/common/AuraFunctions';
import { isWarehouseCountProduct, warehouseCountHelperText, warehouseCountPreview } from '../warehouseCount';
import { useInventoryWording } from '../inventoryWording';
import { getProductSubTypes, getProductPolishes, updateProductSubTypeAndPolishes } from '../../store/productsSlice';
import AuraFreeSoloControlledAutocomplete from '../../../common/AuraFreeSoloControlledAutocomplete';
import { optionKey, withoutKey } from 'app/main/apps/common/optionProps';
import AuraControlledAutocomplete from '../../../common/AuraControlledAutocomplete';
import { getSuppliers } from '../../store/rfqsSlice';
import { filterSuppliers, isInventoryProduct } from '../productSupplier';
import SameNameStockWarning from '../SameNameStockWarning';
import { skuFieldState } from '../productSku';
import { canManageSku } from '../../../settings/sku-codes/skuCodes';

function BasicInfoTab(propsParam) {
  const dispatch = useDispatch();
  const methods = useFormContext();
  const { control, formState, watch, setValue, getValues } = methods;
  // `dirtyFields` tells us which form fields were actually modified by the user.
  const { errors, dirtyFields } = formState;
  const { productColors, productTypes, productSubTypes, productPolishes } = useSelector(({ eCommerceApp }) => eCommerceApp.products)
  const stoneColor = watch('stone_color');
  const type = watch('type');
  const length = watch('length');
  const width = watch('width');
  const height = watch('height');
  const polish = watch('polish');
  const squareFeet = watch('square_feet');
  const unitsInStock = watch('units_in_stock');
  const committed = propsParam?.product?.committed ?? 0;
  const shortFall = propsParam?.product?.short_fall ?? 0;
  const isReadOnly = !propsParam.isManage || propsParam.isCustomer;
  // PGSS-5735: the product's supplier, for staff, on inventory products only (stock, not custom). Follows the
  // stock-product checkbox as it is ticked.
  const isStockProduct = watch('is_stock_product');
  const productName = watch('name');
  const showSupplier = !propsParam.isCustomer
    && isInventoryProduct({ is_stock_product: isStockProduct, is_custom: getValues().is_custom ?? propsParam?.product?.is_custom });
  const suppliersList = useSelector(({ eCommerceApp }) => eCommerceApp.rfqs?.suppliersList) || [];
  const currentUser = useSelector(({ user }) => user);
  const sku = skuFieldState({
    isStockProduct: showSupplier,
    isNew: !propsParam?.product?.id,
    canManage: canManageSku(currentUser),
    isReadOnly,
  });
  useEffect(() => {
    if (showSupplier && !isReadOnly && suppliersList.length === 0) {
      dispatch(getSuppliers({ suppliers: [], params: {} }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSupplier, isReadOnly]);
  const isLesserQuantity = propsParam?.product?.units_in_stock && propsParam?.product?.units_in_stock > Number(getValues().units_in_stock)
  // PGSS-5718: on an existing stock product Total is the warehouse count and Available is worked out from it.
  const usesWarehouseCount = isWarehouseCountProduct(propsParam?.product);
  const warehouseCount = watch('total_quantity');
  // PGSS-5724: In Stock Qty / Warehouse count and the count hint use the Inventory History wording settings.
  const w = useInventoryWording();
  const countPreview = usesWarehouseCount && dirtyFields.total_quantity
    ? warehouseCountPreview(warehouseCount, propsParam?.product)
    : null;

  function onTypeUpdate(event, value) {
    setValue('sub_type', '')
    setValue('polish', '')
    if (value?.name) {
      dispatch(getProductSubTypes({ path: '/api/productSubTypesByProductType', params: { id: value?.id, custom_product: false } }));
      dispatch(getProductPolishes({ path: '/api/polishesByProductType', params: { productTypeId: value?.id, custom_product: false } }))
    } else {
      dispatch(updateProductSubTypeAndPolishes({ data: [] }))
    }
  }

  function onStoneDataUpdate(event, value) {
    if (value && value.id && value.attachment_id) {
      dispatch(getFile(value.attachment_id)).then((response) => {
        const responseData = response ? response.payload : null;
        if (responseData) {
          const stoneColorData = { ...value, attachment: responseData.data.file };
          setValue('stone_color', stoneColorData);
        }
      });
    }
  }

  function stoneValidate() {
    return !!(stoneColor && !stoneColor.name && stoneColor.attachment && stoneColor.attachment.id);
  }

  const getPolish = () => {
    if (polish) {
      if (typeof polish === 'object') {
        return polish.inputValue || polish.name
      }
      return polish
    }
    return ''
  }

  useEffect(() => {
    if (propsParam.isManage) {
      if (length > 0 && width > 0 && height > 0 && stoneColor && type?.name) {
        const stoneColorName = stoneColor?.inputValue ?? stoneColor.name
        const typeName = type?.inputValue ?? type.name
        setValue('name', `${stoneColorName} ${typeName} ${length}x${width}x${height} ${getPolish()}`, { shouldValidate: true });
        setValue('description', `${stoneColorName} ${typeName} ${length}x${width}x${height} ${getPolish()}`, { shouldValidate: true });
      }
    }
  }, [length, width, height, polish, stoneColor, type, setValue, propsParam.isManage]);


  useEffect(() => {
    // Exit early if the form is not in manage mode.
    if (!propsParam.isManage) return;

    // Square feet calculation requires all three dimensions. If any value is missing or invalid, skip calculation.
    if (!(length > 0 && width > 0 && height > 0)) return;

    // Detect whether the USER actually changed any dimension fields.
    // In edit mode, React Hook Form sets default values but does NOT mark them dirty.
    // We only want to auto-calculate when the user manually edits length/width/height.
    const userChangedDimensions = dirtyFields.length || dirtyFields.width || dirtyFields.height;

    // Check if square_feet is missing or not yet populated. This allows initial calculation when the field is empty.
    const squareFeetMissing = squareFeet === null || squareFeet === undefined || squareFeet === "";

    // If the user didn’t change dimensions AND square feet already exists, do nothing — avoid overwriting existing data.
    if (!userChangedDimensions && !squareFeetMissing) return;

    // Calculate square feet ONLY when when User changes length/width/height, OR square_feet is currently missing
    setValue("square_feet", calculateSquareFeet(length, width, height));
  }, [length, width, height, dirtyFields]);

  useEffect(() => {
    // Exit early if the form is not in manage mode.
    if (!propsParam.isManage) return;

    // Detect whether the USER actually changed any units_in_stock field.
    // In edit mode, React Hook Form sets default values but does NOT mark them dirty.
    // We only want to auto-calculate when the user manually edits units_in_stock.
    const userChangedInStock = dirtyFields.units_in_stock;

    // If the user did not change units_in_stock already exists, do nothing — avoid overwriting existing data.
    if (!userChangedInStock) return;

    // Calculate total_quantity ONLY when when User changes units_in_stock
    setValue("total_quantity", calculateProductTotalQuantity(unitsInStock, committed, shortFall));
  }, [unitsInStock, committed, shortFall, dirtyFields]);

  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === "number") {
        e.target.blur();
      }
    };

    document.addEventListener("wheel", handleWheel, { passive: true });

    return () => {
      document.removeEventListener("wheel", handleWheel);
    };
  }, []);

  return (
    <div>
      <div className="flex flex-col md:flex-row w-full">
        <div className="flex w-full">
          <div className="flex flex-col w-full">
            <div className="flex flex-col md:flex-row w-full">
              <div className="flex flex-col w-full md:w-1/2">
                <Controller
                  name="name"
                  control={control}
                  render={({ field, fieldState }) => (
                    <div className="flex gap-10 mt-8 mb-16">
                      <Typography variant="body1" className="font-bold">
                        Name
                      </Typography>
                      <Typography variant="body1" color={fieldState.error ? 'error' : 'textSecondary'} >
                        {fieldState.error ? fieldState.error.message : field.value}
                      </Typography>
                    </div>
                  )}
                />
                <SameNameStockWarning
                  name={productName}
                  productId={propsParam?.product?.id}
                  enabled={showSupplier && !isReadOnly}
                />
              </div>
              <div className="flex w-full md:w-1/2 md:ml-20">
                <div className="flex flex-col w-full mb-16">
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <div className="flex gap-10 mt-8">
                        <Typography variant="body1" className="font-bold">
                          Description
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                          {field.value}
                        </Typography>
                      </div>
                    )}
                  />
                </div>
              </div>

            </div>

            <div className="flex flex-col md:flex-row w-full">
              <div className="flex flex-col md:flex-row w-full md:w-1/2">
              <div className="flex w-full md:w-2/5">
                <div className="flex flex-col w-full mb-16">
                  <AuraFreeSoloControlledAutocomplete
                    control={control}
                    controlName="stone_color"
                    inputValues={productColors}
                    labelName="Stone Color *"
                    readOnly={isReadOnly}
                    onUpdate={onStoneDataUpdate}
                    textClassName="mt-8 mb-0"
                  />
                  {stoneValidate() && <FormHelperText error>Select stone color</FormHelperText>}
                </div>
              </div>
              <div className="flex w-full md:w-1/5 md:ml-16">
                {/* <div className="flex flex-col w-full mb-16"> */}
                <Controller
                  name="length"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className="mt-8 mb-16"
                      id="length"
                      label="Length"
                      variant="outlined"
                      fullWidth
                      size="small"
                      type="number"
                      InputProps={{
                        readOnly: isReadOnly,
                      }}
                      required={propsParam.isManage && !isReadOnly}
                      error={!!errors.length}
                      helperText={errors?.length?.message}
                    />
                  )}
                />
                {/* </div> */}
              </div>
              <div className="flex w-full md:w-1/5 md:ml-16">
                <Controller
                  name="width"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className="mt-8 mb-16"
                      id="width"
                      label="Width"
                      variant="outlined"
                      type="number"
                      fullWidth
                      size="small"
                      InputProps={{
                        readOnly: isReadOnly,
                      }}
                      required={propsParam.isManage && !isReadOnly}
                      error={!!errors.width}
                      helperText={errors?.width?.message}
                    />
                  )}
                />
              </div>
              <div className="flex w-full md:w-1/5 md:ml-16">
                <Controller
                  name="height"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className="mt-8 mb-16"
                      id="height"
                      label="Height"
                      variant="outlined"
                      type="number"
                      fullWidth
                      size="small"
                      InputProps={{
                        readOnly: isReadOnly,
                      }}
                      required={propsParam.isManage && !isReadOnly}
                      error={!!errors.height}
                      helperText={errors?.height?.message}
                    />
                  )}
                />
              </div>
              </div>
              <div className="flex flex-col md:flex-row w-full md:w-1/2">
              <div className="flex w-full md:w-1/3 md:ml-16">
                <AuraFreeSoloControlledAutocomplete
                  control={control}
                  controlName="type"
                  inputValues={productTypes}
                  labelName="Type *"
                  readOnly={isReadOnly}
                  onUpdate={onTypeUpdate}
                />
              </div>
              <div className="flex w-full md:w-1/3 md:ml-16">
                {/* <Controller
                  name="polish"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className="mt-8 mb-16"
                      value={field.value || ''}
                      id="polish"
                      label="Polish"
                      type="text"
                      variant="outlined"
                      fullWidth
                      size="small"
                      InputProps={{
                        readOnly: isReadOnly,
                      }}
                    />
                  )}
                /> */}
                <AuraFreeSoloControlledAutocomplete
                  control={control}
                  controlName="polish"
                  inputValues={productPolishes}
                  labelName="Polish"
                  readOnly={isReadOnly}
                />

              </div>
              <div className="flex w-full md:w-1/3 md:ml-16">
                <AuraFreeSoloControlledAutocomplete
                  control={control}
                  controlName="sub_type"
                  inputValues={productSubTypes}
                  labelName="Sub Type"
                  readOnly={isReadOnly}
                />

              </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row w-full">
              <div className="flex flex-col md:flex-row w-full md:w-1/2">
              <div className="flex w-full md:w-2/5">
                <div className="flex flex-row w-full">
                  <Controller
                    name="units_in_stock"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mt-8 mb-16"
                        id="units_in_stock"
                        label={w('columnInStock')}
                        type="number"
                        variant="outlined"
                        fullWidth
                        size="small"
                        InputProps={{
                          readOnly: isReadOnly || usesWarehouseCount,
                        }}
                        error={!usesWarehouseCount && isLesserQuantity}
                        helperText={!usesWarehouseCount && isLesserQuantity ?
                          'Note : Reducing stock may affect existing orders. Proceed with caution.' :
                          undefined}
                      />
                    )}
                  />
                  <HelperTooltipWithIcon title="Status of a product that is currently available and ready for purchase or immediate delivery, indicating that it is on hand and not out of stock." />
                </div>
              </div>
              <div className="flex w-full md:w-1/5 md:ml-16">
                <Controller
                  name="weight"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className="mt-8 mb-16"
                      id="weight"
                      error={!!errors.weight}
                      required={propsParam.isManage && !isReadOnly}
                      helperText={errors?.weight?.message}
                      label="Weight"
                      variant="outlined"
                      fullWidth
                      size="small"
                      type="number"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">lbs</InputAdornment>
                        ),
                        readOnly: isReadOnly,
                      }}
                    />
                  )}
                />
              </div>
              <div className="flex w-full md:w-1/5 md:ml-16">
                <Controller
                  name="on_order"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className="mt-8 mb-16"
                      id="on_order"
                      label="On Order"
                      type="number"
                      variant="outlined"
                      fullWidth
                      size="small"
                      disabled
                    />
                  )}
                />
                <HelperTooltipWithIcon title="Products that have been requested or ordered by a customer but have not yet been delivered or received." />
              </div>
              <div className="flex w-full md:w-1/5 md:ml-16">
                <Controller
                  name="committed"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className="mt-8 mb-16"
                      id="committed"
                      label="Committed"
                      type="number"
                      variant="outlined"
                      fullWidth
                      size="small"
                      disabled
                    />
                  )}
                />
                <HelperTooltipWithIcon title="Quantity of a product that has been allocated or reserved for specific orders or customers, indicating that these items are no longer available for other orders or uses." />
              </div>
              </div>
              <div className="flex flex-col md:flex-row w-full md:w-1/2">
              <div className="flex w-full md:w-1/4 md:ml-16">
                <Controller
                  name="reorder_point"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className="mt-8 mb-16"
                      id="reorder_point"
                      label="Reorder Point"
                      type="number"
                      variant="outlined"
                      fullWidth
                      size="small"
                      InputProps={{
                        readOnly: isReadOnly,
                      }}
                    />
                  )}
                />
                <HelperTooltipWithIcon title="The minimum inventory level at which a new order should be placed to ensure that stock is replenished before it runs out." />

              </div>
              <div className="flex w-full md:w-1/4 md:ml-16">
                <Controller
                  name="min_package_qty"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      className="mt-8 mb-16"
                      id="min_package_qty"
                      label="Minimum package quantity"
                      type="number"
                      variant="outlined"
                      fullWidth
                      size="small"
                      inputProps={{ min: 1, 'data-testid': 'product-min-package-qty' }}
                      InputProps={{
                        readOnly: isReadOnly,
                      }}
                    />
                  )}
                />
                <HelperTooltipWithIcon title="The supplier's pack size, e.g. 20 per box. Shown on Restock as a guide; orders are never rounded to it automatically." />
              </div>
              <div className="flex w-full md:w-1/4 md:ml-16">
                <Controller
                  name="short_fall"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className="mt-8 mb-16"
                      id="short_fall"
                      label="Short Fall"
                      type="number"
                      variant="outlined"
                      fullWidth
                      size="small"
                      disabled
                    />
                  )}
                />
                <HelperTooltipWithIcon title="Quantity of a particular item in stock is less than what is needed or expected. It indicates a deficiency or shortage in inventory levels." />
              </div>
              <div className="flex w-full md:w-1/4 md:ml-16">
                <div className="flex flex-row w-full">
                  <Controller
                    name="total_quantity"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="mt-8 mb-16"
                        id="total_quantity"
                        label={usesWarehouseCount ? w('warehouseCount') : 'Total Qty'}
                        type="number"
                        variant="outlined"
                        fullWidth
                        size="small"
                        disabled={!usesWarehouseCount || isReadOnly}
                        inputProps={{ min: 0 }}
                        error={Boolean(countPreview && (countPreview.count < 0 || countPreview.released > 0))}
                        helperText={warehouseCountHelperText(countPreview, w)}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="flex w-full md:w-1/4 md:ml-16">
                <Controller
                  name="square_feet"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      className="mt-8 mb-16"
                      id="square_feet"
                      label="Square Feet"
                      variant="outlined"
                      type="number"
                      fullWidth
                      size="small"
                      error={!!errors.square_feet}
                      helperText={errors?.square_feet?.message}
                      disabled
                    />
                  )}
                />
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row w-full">
        <Controller
          name="note"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              className="mt-8 mb-16"
              id="note"
              label="Notes"
              type="text"
              multiline
              rows={3}
              variant="outlined"
              fullWidth
              size="small"
              InputProps={{
                readOnly: isReadOnly,
              }}
            />
          )}
        />
      </div>
      <div className="flex flex-col md:flex-row w-full">
        {/* PGSS-5735: Supplier leads the Category / SKU row (three equal columns) rather than taking a row of its own.
            Without it, Category and SKU split the row in half. Widths are explicit: the md:w-1/8 SKU used to carry is not
            in this project's Tailwind width scale, so it generated no CSS. */}
        {showSupplier && (
          <div className="flex w-full md:w-1/3" data-testid="product-supplier-row">
            <AuraControlledAutocomplete
              inputValues={suppliersList}
              control={control}
              controlName="supplier"
              labelName="Supplier"
              optionCompareField="id"
              filterOptions={filterSuppliers}
              readOnly={isReadOnly}
            />
            <HelperTooltipWithIcon title="The supplier this product is usually bought from. Clear it to remove it." />
          </div>
        )}
        <div className={`flex w-full${showSupplier ? ' md:w-1/3 md:ml-16' : ''}`}>
          <AuraFreeSoloControlledAutocomplete
            control={control}
            controlName="category"
            inputValues={propsParam.categoryTypes}
            labelName="Category"
            readOnly={isReadOnly}
            className={errors && errors?.category?.message ? 'mt-8 mb-0' : 'mt-8 mb-16'}
            renderOption={(renderprops, option) => (
              <li key={optionKey(renderprops)} {...withoutKey(renderprops)} className="py-2 px-8">
                <div className="capitalize">{option.name}</div>
              </li>
            )}
            hideClearable
          />
          {errors && errors?.payment_type?.message && (
            <FormHelperText className="mt-10 text-12 text-center" error={!!errors.payment_type}>
              {errors?.payment_type?.message}
            </FormHelperText>
          )}
        </div>
        <div className={`flex w-full${showSupplier ? ' md:w-1/3' : ''} md:ml-16`}>
          <Controller
            name="sku"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                className="mt-8 mb-16"
                id="sku"
                label="SKU"
                type="text"
                variant="outlined"
                fullWidth
                size="small"
                helperText={sku.helperText || undefined}
                InputProps={{
                  readOnly: sku.readOnly,
                }}
                inputProps={{ 'data-testid': 'product-sku' }}
              />
            )}
          />
        </div>
      </div>

      <div className="flex flex-col md:flex-row w-full">
        <div className="flex w-full md:w-1/8">
          <FormControlLabel
            control={
              <Controller
                name="visible_to_wholesaler"
                control={control}
                defaultValue={false}
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    id="roles-id"
                    label="Visible To Wholesaler"
                    name="visible_to_wholesaler"
                    variant="outlined"
                    checked={value}
                    size="small"
                    disabled={isReadOnly}
                    onChange={(ev) => onChange(ev.target.checked)}
                  />
                )}
              />
            }
            label="Visible To Wholesaler"
          />
          <FormControlLabel
            control={
              <Controller
                name="is_stock_product"
                control={control}
                defaultValue={false}
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    id="roles-id"
                    label="is_stock_product To Wholesaler"
                    name="is_stock_product"
                    variant="outlined"
                    checked={value}
                    size="small"
                    disabled={isReadOnly}
                    onChange={(ev) => onChange(ev.target.checked)}
                  />
                )}
              />
            }
            label="Stock Product"
          />
        </div>
      </div>
    </div>
  );
}

export default BasicInfoTab;
